import "./feedback.scss";

import classNames from "classnames";
import { Dispatch, SetStateAction, useEffect, useState } from "react";

import { ReactComponent as ExclamationIcon } from "@client/components/reusable/SvgIcons/library/exclamation-triangle.svg";
import { ReactComponent as PackageIcon } from "@client/components/reusable/SvgIcons/library/package.svg";
import { ReactComponent as PickupsCheck } from "@client/components/reusable/SvgIcons/library/pickups-check.svg";
import { ReactComponent as PrinterIcon } from "@client/components/reusable/SvgIcons/library/printer.svg";
import { ReactComponent as TruckIcon } from "@client/components/reusable/SvgIcons/library/truck.svg";
import { ReactComponent as ScanIcon } from "@client/components/reusable/SvgIcons/library/upc-scan.svg";
import { useRootContext } from "@client/context/RootContext";
import { sleep } from "@client/helpers/utils";

import SvgIcon from "../reusable/SvgIcons/SvgIcon/SvgIcon";

export default function Feedback() {
	const { modalStore, apiService } = useRootContext();

	function showFeedbackModal() {
		function closeFeedbackModal() {
			modalStore.removeModal("feedback-modal");
		}
		modalStore.addModal({
			id: "feedback-modal",
			content: (
				<FeedbackModal closeFeedbackModal={closeFeedbackModal} />
			) as any,
			showCloseBtn: true,
		});
	}

	return (
		<div className="feedback-container">
			<button className="feedback-btn" onClick={showFeedbackModal}>
				דווח על בעיה{" "}
				<SvgIcon component={ExclamationIcon} width="24" height="24" />
			</button>
		</div>
	);
}

export function FeedbackModal({
	closeFeedbackModal,
}: {
	closeFeedbackModal: () => void;
}) {
	const [selectedFeedback, setSelectedFeedback] = useState<number>(0);
	useEffect(() => {
		if (selectedFeedback === 5) {
			(async function () {
				await sleep(4000);
				closeFeedbackModal();
			})();
		}
	}, [selectedFeedback]);

	return (
		<div className="feedback-modal-container">
			{selectedFeedback !== 5 && (
				<>
					<div className="feedback-modal-title">דווח על בעיה</div>
					<div className="border-line" />
				</>
			)}

			<div
				className={classNames("feedback-modal-content", {
					"feedback-modal-content-done": selectedFeedback === 5,
				})}
			>
				{selectedFeedback === 0 && (
					<Main setSelectedFeedback={setSelectedFeedback} />
				)}
				{selectedFeedback === 1 && (
					<>
						<div className="feedback-request-title">בקשת חומרי אריזה</div>
						<PackagingRequest setSelectedFeedback={setSelectedFeedback} />
					</>
				)}
				{selectedFeedback === 2 && (
					<>
						<div className="feedback-request-title">הערה</div>
						<CourierDidntScan setSelectedFeedback={setSelectedFeedback} />
					</>
				)}
				{selectedFeedback === 3 && (
					<>
						<div className="feedback-request-title">
							נא לציין את המספר טלפון של איש הקשר אליו לפנות לטיפול בתקלה
						</div>
						<PrinterNotWorking setSelectedFeedback={setSelectedFeedback} />
					</>
				)}
				{selectedFeedback === 4 && (
					<>
						<div className="feedback-request-title">הערה</div>
						<CourierDidntArrive setSelectedFeedback={setSelectedFeedback} />
					</>
				)}
				{selectedFeedback === 5 && (
					<div className="feedback-request-sent" onClick={closeFeedbackModal}>
						<PickupsCheck style={{ marginBottom: 10 }} />
						<div className="feedback-request-sent-title">תודה רבה!</div>
						<div className="feedback-request-sent-description">
							הבקשה שלך התקבלה בהצלחה, ניצור קשר בקרוב.
						</div>
					</div>
				)}
			</div>
		</div>
	);
}

function Main({
	setSelectedFeedback,
}: {
	setSelectedFeedback: Dispatch<SetStateAction<number>>;
}) {
	return (
		<div className="feedback-btn-container">
			<button className="feedback-btn" onClick={() => setSelectedFeedback(1)}>
				<SvgIcon component={PackageIcon} width="32" height="32" />
				<span>בקשת חומרי אריזה</span>
			</button>

			<button className="feedback-btn" onClick={() => setSelectedFeedback(2)}>
				<SvgIcon component={ScanIcon} width="32" height="32" />
				<span>שליח לא סרק הזמנה</span>
			</button>

			<button className="feedback-btn" onClick={() => setSelectedFeedback(3)}>
				<SvgIcon component={PrinterIcon} width="32" height="32" />
				<span>מדפסת לא עובדת</span>
			</button>

			<button className="feedback-btn" onClick={() => setSelectedFeedback(4)}>
				<SvgIcon component={TruckIcon} width="32" height="32" />
				<span>שליח לא הגיע</span>
			</button>
		</div>
	);
}

function Controls({
	disabledSubmit,
	setSelectedFeedback,
	handleSubmit,
}: {
	disabledSubmit?: boolean;
	setSelectedFeedback: Dispatch<SetStateAction<number>>;
	handleSubmit: () => Promise<void>;
}) {
	const [submitted, setSubmitted] = useState<boolean>(false);
	async function _handleSubmit() {
		if (submitted) {
			return;
		}
		setSubmitted(true);
		await handleSubmit();
		setSubmitted(false);
	}
	return (
		<div className="controls-container">
			<button
				className={classNames("action-btn submit-btn", {
					disabled: disabledSubmit,
				})}
				onClick={_handleSubmit}
				disabled={disabledSubmit}
			>
				שלח
			</button>
			<button
				className="action-btn back-btn"
				onClick={() => setSelectedFeedback(0)}
			>
				חזור
			</button>
		</div>
	);
}

function PackagingRequest({
	setSelectedFeedback,
}: {
	setSelectedFeedback: Dispatch<SetStateAction<number>>;
}) {
	const { apiService, vendorStore } = useRootContext();

	const [boxes, setBoxes] = useState<string[]>([]);
	const [bags, setBags] = useState<string[]>([]);
	const [stickers, setStickers] = useState<boolean>(false);

	const toggleSelection = (
		value: string,
		selected: string[],
		setSelected: Dispatch<SetStateAction<string[]>>
	) => {
		if (selected.includes(value)) {
			setSelected(selected.filter((v) => v !== value));
		} else {
			setSelected([...selected, value]);
		}
	};

	const handleSubmit = async () => {
		await apiService.api("/vendor/report-issue", {
			vendor_id: vendorStore.vendorDetails?.id,
			issue_type: "packaging_material",
			requested_packages: {
				boxes_small: boxes.includes("S") ? 1 : 0,
				boxes_medium: boxes.includes("M") ? 1 : 0,
				boxes_large: boxes.includes("L") ? 1 : 0,
				boxes_xlarge: boxes.includes("XL") ? 1 : 0,
				bags_small: bags.includes("S") ? 1 : 0,
				bags_medium: bags.includes("M") ? 1 : 0,
				bags_large: bags.includes("L") ? 1 : 0,
				stickers: stickers ? 1 : 0,
			},
		});

		setSelectedFeedback(5);
	};

	return (
		<>
			<div className="packaging-request">
				<div className="packaging-row">
					<span className="row-label">קרטונים</span>
					<div className="toggle-group">
						{["S", "M", "L", "XL"].map((size) => (
							<button
								key={size}
								className={`toggle-btn ${
									boxes.includes(size) ? "selected" : ""
								}`}
								onClick={() => toggleSelection(size, boxes, setBoxes)}
							>
								<span>{size}</span>
								<span
									className={`checkbox ${
										boxes.includes(size) ? "checked" : ""
									}`}
								>
									{boxes.includes(size) && (
										<svg
											width="14"
											height="14"
											viewBox="0 0 24 24"
											fill="none"
											stroke="white"
											strokeWidth="3"
										>
											<polyline points="20 6 9 17 4 12" />
										</svg>
									)}
								</span>
							</button>
						))}
					</div>
				</div>

				<div className="packaging-row">
					<span className="row-label">שקיות</span>
					<div className="toggle-group">
						{["S", "M", "L"].map((size) => (
							<button
								key={size}
								className={`toggle-btn ${
									bags.includes(size) ? "selected" : ""
								}`}
								onClick={() => toggleSelection(size, bags, setBags)}
							>
								<span>{size}</span>
								<span
									className={`checkbox ${bags.includes(size) ? "checked" : ""}`}
								>
									{bags.includes(size) && (
										<svg
											width="14"
											height="14"
											viewBox="0 0 24 24"
											fill="none"
											stroke="white"
											strokeWidth="3"
										>
											<polyline points="20 6 9 17 4 12" />
										</svg>
									)}
								</span>
							</button>
						))}
					</div>
				</div>

				<div className="packaging-row">
					<span className="row-label">מדבקות</span>
					<div className="toggle-group">
						<button
							className={`toggle-btn ${stickers ? "selected" : ""}`}
							onClick={() => setStickers(!stickers)}
						>
							<span>הוסף</span>
							<span className={`checkbox ${stickers ? "checked" : ""}`}>
								{stickers && (
									<svg
										width="14"
										height="14"
										viewBox="0 0 24 24"
										fill="none"
										stroke="white"
										strokeWidth="3"
									>
										<polyline points="20 6 9 17 4 12" />
									</svg>
								)}
							</span>
						</button>
					</div>
				</div>
			</div>
			<Controls
				disabledSubmit={boxes.length === 0 && bags.length === 0 && !stickers}
				setSelectedFeedback={setSelectedFeedback}
				handleSubmit={handleSubmit}
			/>
		</>
	);
}
function CourierDidntScan({
	setSelectedFeedback,
}: {
	setSelectedFeedback: Dispatch<SetStateAction<number>>;
}) {
	const { apiService, vendorStore } = useRootContext();

	const [feedback, setFeedback] = useState<string>("");

	const handleSubmit = async () => {
		await apiService.api("/vendor/report-issue", {
			vendor_id: vendorStore.vendorDetails?.id,
			issue_type: "carrier_no_scan",
			comment: feedback,
		});

		setSelectedFeedback(5);
	};

	return (
		<>
			<div className="scan-request">
				<textarea
					value={feedback}
					onChange={(e) => setFeedback(e.target.value)}
					placeholder="הערה (אופציונלי)"
				/>
			</div>
			<Controls
				setSelectedFeedback={setSelectedFeedback}
				handleSubmit={handleSubmit}
			/>
		</>
	);
}

function isValidIsraeliPhone(phone: string): boolean {
	// Remove spaces, dashes, and parentheses
	const cleaned = phone.replace(/[\s\-()]/g, "");

	// Israeli phone patterns:
	// +972 followed by 9 digits (international format)
	// 972 followed by 9 digits (international without +)
	// 0 followed by 8-9 digits (local format, landline or mobile)
	// +0 followed by 8-9 digits (local with + prefix)
	// 8-9 digits without prefix
	const patterns = [
		/^\+972[1-9]\d{8}$/, // +972XXXXXXXXX
		/^972[1-9]\d{8}$/, // 972XXXXXXXXX
		/^0[1-9]\d{7,8}$/, // 0XXXXXXXX or 0XXXXXXXXX (9-10 digits)
		/^\+0[1-9]\d{7,8}$/, // +0XXXXXXXX or +0XXXXXXXXX (+ with local)
		/^[1-9]\d{7,8}$/, // XXXXXXXX or XXXXXXXXX (8-9 digits, no prefix)
	];

	return patterns.some((pattern) => pattern.test(cleaned));
}

function PrinterNotWorking({
	setSelectedFeedback,
}: {
	setSelectedFeedback: Dispatch<SetStateAction<number>>;
}) {
	const { apiService, vendorStore } = useRootContext();
	const [phone, setPhone] = useState<string>("");
	const [touched, setTouched] = useState<boolean>(false);

	const isValid = isValidIsraeliPhone(phone);
	const showError = touched && phone && !isValid;

	const handleSubmit = async () => {
		if (!isValid) return;

		await apiService.api("/vendor/report-issue", {
			vendor_id: vendorStore.vendorDetails?.id,
			issue_type: "printer_not_working",
			phone,
		});

		setSelectedFeedback(5);
	};

	return (
		<>
			<div className="printer-request">
				<input
					type="tel"
					value={phone}
					onChange={(e) => {
						// Only allow digits, +, -, spaces, parentheses
						const filtered = e.target.value.replace(/[^\d+\-\s()]/g, "");
						setPhone(filtered);
					}}
					onBlur={() => setTouched(true)}
					placeholder="מספר טלפון"
					className={showError ? "input-error" : ""}
				/>
				{showError && <div className="error-message">מספר טלפון לא תקין</div>}
			</div>
			<Controls
				setSelectedFeedback={setSelectedFeedback}
				handleSubmit={handleSubmit}
				disabledSubmit={!phone || !isValid}
			/>
		</>
	);
}
function isValidEmail(email: string): boolean {
	const pattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
	return pattern.test(email);
}

function CourierDidntArrive({
	setSelectedFeedback,
}: {
	setSelectedFeedback: Dispatch<SetStateAction<number>>;
}) {
	const { apiService, vendorStore } = useRootContext();
	const [comment, setComment] = useState<string>("");

	const handleSubmit = async () => {
		await apiService.api("/vendor/report-issue", {
			vendor_id: vendorStore.vendorDetails?.id,
			issue_type: "carrier_not_arrived",
			comment: comment,
		});

		setSelectedFeedback(5);
	};

	return (
		<>
			<div className="courier-request">
				<textarea
					value={comment}
					onChange={(e) => setComment(e.target.value)}
					placeholder="הערה (אופציונלי)"
				/>
			</div>
			<Controls
				setSelectedFeedback={setSelectedFeedback}
				handleSubmit={handleSubmit}
			/>
		</>
	);
}
